export function validatePicoInfo(info, schema = PicoEventSchema) {
  if (typeof info !== "object" || info == null || Array.isArray(info)) {
    throw new Error(`info must be a plain object`);
  }
  for (const key of Object.keys(info)) {
    const spec = schema[key];
    if (!spec) throw new Error(`${key}: not defined in schema`);
    validateValue(key, info[key], spec);
  }
}

const isISODate = v => typeof v === "string" && !Number.isNaN(Date.parse(v));

const validateValue = (path, value, spec) => {
  if (value === undefined) return;
  if (value === null) return;

  if (spec.repeated) {
    if (!Array.isArray(value)) throw new Error(`${path}: must be an array`);
    value.forEach((item, i) => validateValue(`${path}[${i}]`, item, { ...spec, repeated: false }));
    return;
  }

  switch (spec.type) {
    case BigQueryTypes.STRING:
      if (typeof value !== "string") throw new Error(`${path}: must be STRING`);
      break;
    case BigQueryTypes.BOOLEAN:
      if (typeof value !== "boolean") throw new Error(`${path}: must be BOOLEAN`);
      break;
    case BigQueryTypes.FLOAT:
      if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path}: must be number (FLOAT)`);
      break;
    case BigQueryTypes.INTEGER:
      if (!Number.isInteger(value)) throw new Error(`${path}: must be integer (INTEGER)`);
      break;
    case BigQueryTypes.TIMESTAMP: {
      const ok = value instanceof Date || (typeof value === "number" && Number.isFinite(value)) || isISODate(value);
      if (!ok) throw new Error(`${path}: must be TIMESTAMP (Date | epoch ms | ISO string)`);
      break;
    }
    case BigQueryTypes.RECORD: {
      if (typeof value !== "object" || Array.isArray(value)) throw new Error(`${path}: must be object (RECORD)`);
      const fields = spec.fields || {};
      for (const k of Object.keys(value)) {
        const child = fields[k];
        if (!child) throw new Error(`${path}.${k}: not defined in schema`);
        validateValue(`${path}.${k}`, value[k], child);
      }
      break;
    }
    default:
      throw new Error(`${path}: unknown schema type`);
  }
}

export const BigQueryTypes = { STRING: "STRING", BOOLEAN: "BOOLEAN", FLOAT: "FLOAT", INTEGER: "INTEGER", TIMESTAMP: "TIMESTAMP", RECORD: "RECORD" };

const PicoEventSchema = {
  post_type: { type: BigQueryTypes.STRING },
  post_published_manually: { type: BigQueryTypes.BOOLEAN },
  owner_id: { type: BigQueryTypes.STRING },
  created_by_id: { type: BigQueryTypes.STRING },
  hashtag: { type: BigQueryTypes.STRING },
  identity_id: { type: BigQueryTypes.STRING },
  identity_provider: { type: BigQueryTypes.STRING },
  identity_account_changed: { type: BigQueryTypes.BOOLEAN },
  identity_manual_connection: { type: BigQueryTypes.BOOLEAN },
  hashtag_collection_id: { type: BigQueryTypes.STRING },
  calendar_invite_collaborator_user_id: { type: BigQueryTypes.STRING },
  deleted_account_user_id: { type: BigQueryTypes.STRING },
  scheduling_slot_id: { type: BigQueryTypes.STRING },
  scheduling_slot_hour: { type: BigQueryTypes.FLOAT },
  scheduling_slot_minute: { type: BigQueryTypes.FLOAT },
  scheduling_slot_days: { type: BigQueryTypes.STRING },
  nor_checkbox_enabled: { type: BigQueryTypes.BOOLEAN },
  to_calendar_ids: { type: BigQueryTypes.STRING },
  entry_point: { type: BigQueryTypes.STRING },
  state_event: { type: BigQueryTypes.STRING },
  from_state: { type: BigQueryTypes.STRING },
  to_state: { type: BigQueryTypes.STRING },
  bulk_update: { type: BigQueryTypes.BOOLEAN },
  previous_assigned_to_ids: { type: BigQueryTypes.STRING },
  assigned_to_ids: { type: BigQueryTypes.STRING },
  calendar_logo_image_entry_point: { type: BigQueryTypes.STRING },
  calendar_approval_workflow: { type: BigQueryTypes.STRING },
  connect_social_account_platform_name: { type: BigQueryTypes.STRING },
  connect_social_account_entry_point: { type: BigQueryTypes.STRING },
  providers_were: { type: BigQueryTypes.STRING },
  providers: { type: BigQueryTypes.STRING },
  publish_id: { type: BigQueryTypes.FLOAT },
  previous_state: { type: BigQueryTypes.STRING },
  promotions_count: { type: BigQueryTypes.FLOAT },
  failure_message: { type: BigQueryTypes.STRING },
  failure_exception_type: { type: BigQueryTypes.STRING },
  failure_exception_message: { type: BigQueryTypes.STRING },
  analytics_report_scheduling_id: { type: BigQueryTypes.STRING },
  analytics_report_scheduling_services: { type: BigQueryTypes.STRING },
  analytics_report_scheduling_shortened_urls: { type: BigQueryTypes.BOOLEAN },
  analytics_report_scheduling_interval: { type: BigQueryTypes.STRING },
  analytics_report_scheduling_recipient_count: { type: BigQueryTypes.FLOAT },
  source: { type: BigQueryTypes.STRING },
  from_page: { type: BigQueryTypes.STRING },
  to_calendar_id: { type: BigQueryTypes.FLOAT },
  instances: { type: BigQueryTypes.FLOAT },
  date: { type: BigQueryTypes.STRING },
  date_end: { type: BigQueryTypes.STRING },
  repeat: { type: BigQueryTypes.BOOLEAN },
  repeat_interval: { type: BigQueryTypes.FLOAT },
  repeat_periodicity: { type: BigQueryTypes.STRING },
  reasons: {
    type: BigQueryTypes.RECORD,
    fields: {
      invalid_state: { type: BigQueryTypes.STRING },
      invalid_label: { type: BigQueryTypes.STRING },
      too_many_rows: { type: BigQueryTypes.STRING },
      invalid_date: { type: BigQueryTypes.STRING },
      invalid_headers: { type: BigQueryTypes.STRING },
      no_rows: { type: BigQueryTypes.STRING },
      invalid_media: { type: BigQueryTypes.STRING },
      invalid_media_count: { type: BigQueryTypes.STRING },
      invalid_shorten_urls: { type: BigQueryTypes.STRING },
      invalid_service: { type: BigQueryTypes.STRING },
      missing_copy: { type: BigQueryTypes.STRING },
      __invalid_byte_sequence_in_utf_8_in_line_1__: { type: BigQueryTypes.STRING }
    }
  },
  post_ids: { type: BigQueryTypes.STRING },
  draft_idea_id: { type: BigQueryTypes.STRING },
  convert_idea_to_post_location: { type: BigQueryTypes.STRING },
  duplicate_post_entry_point: { type: BigQueryTypes.STRING },
  duplicate_post_repeating: { type: BigQueryTypes.BOOLEAN },
  bulk_upload_location: { type: BigQueryTypes.STRING },
  onboarding_survey_id: { type: BigQueryTypes.STRING },
  onboarding_survey_question_copy: { type: BigQueryTypes.STRING },
  onboarding_survey_selected_options: { type: BigQueryTypes.STRING },
  onboarding_survey_open_answer: { type: BigQueryTypes.STRING },
  post_idea_source: { type: BigQueryTypes.STRING },
  post_idea_recurs: { type: BigQueryTypes.STRING },
  parent_url: { type: BigQueryTypes.STRING },
  feature_codes: { type: BigQueryTypes.STRING },
  ai_caption_location: { type: BigQueryTypes.STRING },
  ai_caption_mode: { type: BigQueryTypes.STRING },
  ai_caption_input_caption: { type: BigQueryTypes.STRING },
  ai_caption_prompt: { type: BigQueryTypes.STRING },
  ai_caption_length: { type: BigQueryTypes.STRING },
  ai_caption_translate_to: { type: BigQueryTypes.STRING },
  ai_caption_add_hashtags: { type: BigQueryTypes.BOOLEAN },
  ai_caption_output_caption: { type: BigQueryTypes.STRING },
  cloned_from_post_idea_id: { type: BigQueryTypes.STRING },
  calendar_owner_id: { type: BigQueryTypes.STRING },
  invite_id: { type: BigQueryTypes.STRING },
  offer_type: { type: BigQueryTypes.STRING },
  offer_duration: { type: BigQueryTypes.FLOAT },
  periodicity_selected: { type: BigQueryTypes.STRING },
  post_quick_adding: { type: BigQueryTypes.BOOLEAN },
  post_providers: { type: BigQueryTypes.STRING },
  post_clone_count: { type: BigQueryTypes.FLOAT },
  post_promoted: { type: BigQueryTypes.BOOLEAN },
  post_state_event: { type: BigQueryTypes.STRING },
  provider: { type: BigQueryTypes.STRING },
  post_state_was: { type: BigQueryTypes.STRING },
  post_state: { type: BigQueryTypes.STRING },
  post_providers_were: { type: BigQueryTypes.STRING },
  post_created_at: { type: BigQueryTypes.TIMESTAMP },
  post_bulk_delete: { type: BigQueryTypes.BOOLEAN },
  post_provider: { type: BigQueryTypes.STRING },
  post_publish_success: { type: BigQueryTypes.BOOLEAN },
  post_template_id: { type: BigQueryTypes.STRING },
  post_assigned_to_ids: { type: BigQueryTypes.STRING },
  calendar_id: { type: BigQueryTypes.STRING },
  billing_cycle: { type: BigQueryTypes.STRING },
  post_id: { type: BigQueryTypes.STRING },
  account_setting_page_type: { type: BigQueryTypes.STRING },
  paywall_trigger: { type: BigQueryTypes.STRING },
  paywall_type: { type: BigQueryTypes.STRING },
  usage_threshold: { type: BigQueryTypes.STRING },
  is_collaborator: { type: BigQueryTypes.BOOLEAN },
  plans_displayed: { type: BigQueryTypes.STRING },
  plan_code: { type: BigQueryTypes.STRING },
  skip_trial: { type: BigQueryTypes.BOOLEAN },
  plan_periodicity: { type: BigQueryTypes.STRING },
  addon_code: { type: BigQueryTypes.STRING },
  current_units: { type: BigQueryTypes.INTEGER },
  new_units: { type: BigQueryTypes.INTEGER },
  is_upgrade: { type: BigQueryTypes.BOOLEAN },
  calendar_export_date_start: { type: BigQueryTypes.TIMESTAMP },
  calendar_export_date_end: { type: BigQueryTypes.TIMESTAMP },
  stripe_subscription_id: { type: BigQueryTypes.STRING },
  stripe_invoice_id: { type: BigQueryTypes.STRING },
  plan_change_effective_at: { type: BigQueryTypes.TIMESTAMP },
  previous_plan_code: { type: BigQueryTypes.STRING },
  previous_plan_periodicity: { type: BigQueryTypes.STRING },
  stripe_previous_subscription_id: { type: BigQueryTypes.STRING },
  stripe_previous_invoice_id: { type: BigQueryTypes.STRING },
  exit_survey_reason_code: { type: BigQueryTypes.STRING },
  exit_survey_other_reason: { type: BigQueryTypes.STRING },
  exit_survey_explanation: { type: BigQueryTypes.STRING },
  exit_survey_usability_reason: { type: BigQueryTypes.STRING },
  exit_survey_alternative_solution: { type: BigQueryTypes.STRING },
  exit_survey_specified_alternative_solution: { type: BigQueryTypes.STRING },
  exit_survey_specified_other_reason: { type: BigQueryTypes.STRING },
  exit_survey_missing_feature: { type: BigQueryTypes.STRING },
  exit_survey_specified_missing_feature: { type: BigQueryTypes.STRING },
  terms_of_service_version: { type: BigQueryTypes.STRING },
  social_platform: { type: BigQueryTypes.STRING },
  user_id: { type: BigQueryTypes.FLOAT },
  referrer_url: { type: BigQueryTypes.STRING },
  plan_change_entry_button: { type: BigQueryTypes.STRING },
  calendar_invite_calendar_id: { type: BigQueryTypes.STRING },
  calendar_invite_collaborator_role: { type: BigQueryTypes.STRING },
  calendar_invite_sender_user_id: { type: BigQueryTypes.STRING },
  post_idea_id: { type: BigQueryTypes.FLOAT },
  post_idea_url: { type: BigQueryTypes.STRING },
  post_idea_view_cell_date: { type: BigQueryTypes.TIMESTAMP },
  location: { type: BigQueryTypes.STRING },
  service: { type: BigQueryTypes.STRING },
  hour: { type: BigQueryTypes.FLOAT },
  new_idea_button_clicked_location: { type: BigQueryTypes.STRING },
  ideas_board_column_index: { type: BigQueryTypes.STRING },
  export_format: { type: BigQueryTypes.STRING },
  refund_request_refund_type: { type: BigQueryTypes.STRING },
  error: { type: BigQueryTypes.STRING },
  refund_request_error_message: { type: BigQueryTypes.STRING },
  from_subscription_panel_code: { type: BigQueryTypes.STRING },
  to_subscription_panel_code: { type: BigQueryTypes.STRING },
  plan_name: { type: BigQueryTypes.STRING },
  plan_cycle: { type: BigQueryTypes.STRING },
  addon_selections: { type: BigQueryTypes.STRING },
  loomly_ai_ui_enabled: { type: BigQueryTypes.BOOLEAN },
  loomly_ai_enabled: { type: BigQueryTypes.BOOLEAN },
  sidebar_expanded: { type: BigQueryTypes.BOOLEAN },
  message_length: { type: BigQueryTypes.INTEGER },
  navigation_item: { type: BigQueryTypes.STRING },
  topbar_item: { type: BigQueryTypes.STRING },
  calendar_id_switched_to: { type: BigQueryTypes.STRING },
  open: { type: BigQueryTypes.BOOLEAN },
  ai_feature: { type: BigQueryTypes.STRING },
  trigger_source: { type: BigQueryTypes.STRING },
  trigger_feature: { type: BigQueryTypes.STRING },
  page_context: { type: BigQueryTypes.STRING },
  from_conversation_id: { type: BigQueryTypes.FLOAT },
  current_conversation_id: { type: BigQueryTypes.STRING },
  selected_conversation_id: { type: BigQueryTypes.STRING },
  deleted_conversation_id: { type: BigQueryTypes.STRING },
  upvoted: { type: BigQueryTypes.BOOLEAN },
  type: { type: BigQueryTypes.STRING },
  interaction_type: { type: BigQueryTypes.STRING },
  resource_id: { type: BigQueryTypes.STRING },
  form_type: { type: BigQueryTypes.STRING },
  has_context: { type: BigQueryTypes.BOOLEAN },
  is_retry: { type: BigQueryTypes.BOOLEAN },
  reason: { type: BigQueryTypes.STRING },
  mock_message_length: { type: BigQueryTypes.INTEGER },
  is_demo: { type: BigQueryTypes.BOOLEAN },
  website_edited: { type: BigQueryTypes.BOOLEAN },
  colors_edited: { type: BigQueryTypes.BOOLEAN },
  guidelines_edited: { type: BigQueryTypes.BOOLEAN },
  competitor_deleted: { type: BigQueryTypes.BOOLEAN },
  brand_analysis_status: { type: BigQueryTypes.STRING },
  platform: { type: BigQueryTypes.STRING },
  selected: { type: BigQueryTypes.BOOLEAN },
  competitor_count: { type: BigQueryTypes.INTEGER },
  platform_count: { type: BigQueryTypes.INTEGER },
  platforms: { type: BigQueryTypes.STRING },
  source_filter: { type: BigQueryTypes.STRING },
  date_from: { type: BigQueryTypes.STRING },
  date_to: { type: BigQueryTypes.STRING },
  attach_source: { type: BigQueryTypes.STRING },
  from_chat: { type: BigQueryTypes.BOOLEAN },
  is_side_chat: { type: BigQueryTypes.BOOLEAN },
  view_number: { type: BigQueryTypes.INTEGER },
  code: { type: BigQueryTypes.STRING },
  enabled_providers: { type: BigQueryTypes.STRING },
  enabled_provider_count: { type: BigQueryTypes.INTEGER },
  response: { type: BigQueryTypes.STRING },
  action: { type: BigQueryTypes.STRING },
  feedback: { type: BigQueryTypes.STRING },
  cta_mode: { type: BigQueryTypes.STRING },
  dismiss_type: { type: BigQueryTypes.STRING },
  chart_type: { type: BigQueryTypes.STRING },
  chartType: { type: BigQueryTypes.STRING },
  message_id: { type: BigQueryTypes.STRING },
  agent_name: { type: BigQueryTypes.STRING },
  chat_open: { type: BigQueryTypes.BOOLEAN },
  post_draft_context: { type: BigQueryTypes.STRING },
  analytics_context: { type: BigQueryTypes.STRING },
  target_language: { type: BigQueryTypes.STRING },
  run_id: { type: BigQueryTypes.STRING },
  job_count: { type: BigQueryTypes.INTEGER },
  analytics_source: { type: BigQueryTypes.STRING },
  variant: { type: BigQueryTypes.STRING },
  count: { type: BigQueryTypes.INTEGER },
  filter_type: { type: BigQueryTypes.STRING },
  filter_value: { type: BigQueryTypes.STRING },
  page_id: { type: BigQueryTypes.STRING },
  blocks_count: { type: BigQueryTypes.INTEGER },
  is_loomly_branding_hidden: { type: BigQueryTypes.BOOLEAN },
  violation_key: { type: BigQueryTypes.STRING },
};
